/**
 * The fixed trim-sheet layout (plan §2.8) turned into shader lookup data (pure; tested). The decal atlas needs no
 * layout here: tex.decals carries per-texel flags in RGB (A6), read directly by the decal material.
 * `assets-src/trim-layout.json` is the single source of the per-band paint and emissive flags: the hull shader
 * decodes the band from TEXCOORD_0.v through a 16-cell table (every band edge is a multiple of 64 px).
 */
import { Color } from 'three/webgpu'
import { z } from 'zod'
import trimLayoutJson from '../../../../assets-src/trim-layout.json'
import type { LoadedTextureSet } from '../../../assets/loader'
import type { MaterialsFile } from '../data'

const trimLayoutSchema = z.looseObject({
  size: z.tuple([z.number().int().positive(), z.number().int().positive()]),
  texelsPerMeter: z.number().positive(),
  bands: z
    .array(
      z.looseObject({
        id: z.string(),
        v: z.tuple([z.number().int().nonnegative(), z.number().int().positive()]),
        paint: z.boolean(),
        emissive: z.boolean(),
      }),
    )
    .min(1),
})

export type TrimLayout = z.infer<typeof trimLayoutSchema>
export type TrimBand = TrimLayout['bands'][number]

export const TRIM_LAYOUT: TrimLayout = trimLayoutSchema.parse(trimLayoutJson)

/** Lookup cells over V (16 × 64 px on a 1024² sheet). */
export const BAND_CELLS = 16

/** What the hull shader does with a band (the `w` of the second table vector). */
export const BAND_KIND = { plainPaint: 0, paintable: 1, lightStrip: 2, nozzleHeat: 3, unpainted: 4 } as const

/** The band containing V (0..1 from the top, glTF convention), or null outside every band. */
export function bandAtV(layout: TrimLayout, v: number): TrimBand | null {
  const px = v * layout.size[1]
  for (const b of layout.bands) if (px >= b.v[0] && px < b.v[1]) return b
  return null
}

/**
 * Two vec4 per 64 px cell: A = (fallback albedo rgb linear, paint flag), B = (metalness, roughness, emissive flag, kind).
 * Paintable bands keep the paint (their albedo slot is unused); unpainted bands carry the style guide §4 class values
 * from materials.json `trim.bandClasses`, used when the trim set is a carrier (contract §8.3 rule 7).
 */
export function buildBandTable(layout: TrimLayout, data: MaterialsFile): Float32Array {
  const cellPx = layout.size[1] / BAND_CELLS
  if (!Number.isInteger(cellPx)) throw new Error('trim layout: sheet height must be a multiple of 16 px')
  const out = new Float32Array(BAND_CELLS * 8)
  const c = new Color()
  for (let i = 0; i < BAND_CELLS; i++) {
    const band = bandAtV(layout, (i + 0.5) / BAND_CELLS)
    if (!band) throw new Error(`trim layout: cell ${i} is in no band`)
    if (band.v[0] % cellPx !== 0 || band.v[1] % cellPx !== 0) throw new Error(`trim layout: band ${band.id} is not on the ${cellPx} px grid`)
    let kind: number = band.paint ? (band.id === 'plain_paint' ? BAND_KIND.plainPaint : BAND_KIND.paintable) : BAND_KIND.unpainted
    if (band.emissive) kind = BAND_KIND.lightStrip
    else if (band.id === 'nozzle_heat') kind = BAND_KIND.nozzleHeat
    const cls = data.trim.bandClasses[band.id]
    if (!band.paint && !cls) throw new Error(`materials.json trim.bandClasses has no entry for unpainted band ${band.id}`)
    c.set(cls ? cls.albedo : '#808080')
    out.set([c.r, c.g, c.b, band.paint ? 1 : 0, cls ? cls.metalness : 0, cls ? cls.roughness : 0.5, band.emissive ? 1 : 0, kind], i * 8)
  }
  return out
}

/** A texture set counts as authored (not a P0 carrier) when its largest map reaches `trim.authoredMinPx`. */
export function isAuthoredSet(set: LoadedTextureSet | undefined, minPx: number): set is LoadedTextureSet {
  return set !== undefined && Math.max(set.entry.size[0], set.entry.size[1]) >= minPx
}

/** Paint zone of an ECL_Mask.a value (contract §2.7): 0 primary, 1 secondary, 2 accent, 3 bare metal. Mirrors the shader. */
export function decodeZone(a: number): number {
  return Math.min(3, Math.max(0, Math.round(a * 3)))
}
